import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import { useSeo } from "@/hooks/useSeo";
import { formatDuration, formatUpdated, getMovieById, getRelatedMovies } from "@/lib/movie";
import PlayerDemo from "./components/PlayerDemo";
import ShareDialog from "./components/ShareDialog";

export default function Detail() {
  const { id } = useParams();
  const movie = getMovieById(id);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useSeo({
    title: movie ? `${movie.title}高清在线观看 - 蜜桃成熟时在线观看` : "影片信息暂不可用 - 蜜桃成熟时在线观看",
    description: movie
      ? `${movie.title}（${movie.year}，${movie.region}）${movie.genres.join("/")}电影，导演${movie.director}，主演${movie.cast.join("、")}。${movie.synopsis.slice(0, 60)}……`
      : "该影片信息暂不可用，可能是链接有误或影片已下架，请返回分类页浏览其他高清电影。",
    keywords: movie
      ? `${movie.title},${movie.title}在线观看,${movie.genres.join(",")},蜜桃成熟时在线观看,高清电影`
      : "影片信息暂不可用,蜜桃成熟时在线观看",
  });

  if (!movie) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className="ri-file-unknow-line text-3xl w-7 h-7 flex items-center justify-center" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">影片信息暂不可用</h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            该影片可能已下架，或者详情链接有误。你可以回到分类页按条件重新筛选，也可以在首页看看最新上架的片单。
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/category"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-grid-line text-base w-4 h-4 flex items-center justify-center" />
              前往电影分类
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              返回首页
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedMovies(movie, 4);

  const metaRows = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join(" / ") },
    { label: "类型", value: movie.genres.join(" / ") },
    { label: "地区", value: movie.region },
    { label: "年份", value: `${movie.year} 年` },
    { label: "时长", value: formatDuration(movie.duration) },
    { label: "更新时间", value: formatUpdated(movie.updatedAt) },
  ];

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto flex w-full max-w-[1280px] flex-wrap items-center justify-between gap-3 px-4 py-4 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "电影分类", to: "/category" },
              { label: movie.title },
            ]}
          />
          <Link
            to="/category"
            className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-700"
          >
            <i className="ri-arrow-left-line text-sm w-4 h-4 flex items-center justify-center" />
            返回分类页
          </Link>
        </div>
      </div>

      <section className="relative overflow-hidden bg-foreground-950">
        <img
          src={movie.poster}
          alt={`${movie.title} 背景图`}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover object-top opacity-25 blur-[3px]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/95 via-foreground-950/85 to-foreground-950/60" />

        <div className="relative mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <div className="flex flex-col gap-7 md:flex-row md:gap-10">
            <div className="w-[168px] shrink-0 overflow-hidden rounded-lg border border-background-50/15 sm:w-[210px] md:w-[260px]">
              <div className="aspect-[2/3] w-full">
                <img
                  src={movie.poster}
                  alt={`${movie.title} 海报`}
                  title={`${movie.title} 高清在线观看`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                {movie.tags.map((tag, index) => (
                  <span
                    key={tag}
                    className={`rounded-full px-2.5 py-1 text-[11px] ${
                      index === 0
                        ? "bg-primary-500/15 text-primary-300"
                        : "border border-background-50/20 text-background-300"
                    }`}
                  >
                    {tag}
                  </span>
                ))}
                <span className="rounded-full border border-background-50/20 px-2.5 py-1 font-label text-[11px] text-background-300">
                  演示片库
                </span>
              </div>

              <h1 className="mt-4 font-heading text-2xl font-bold leading-tight text-background-50 md:text-4xl">
                {movie.title}
              </h1>
              <p className="mt-2 font-label text-xs uppercase tracking-[0.18em] text-background-400">
                {movie.enTitle} · {movie.year}
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
                <span className="flex items-baseline gap-1">
                  <i className="ri-star-fill text-sm w-4 h-4 flex items-center justify-center text-accent-400" />
                  <span className="font-label text-xl font-bold text-accent-400">
                    {movie.rating.toFixed(1)}
                  </span>
                  <span className="font-label text-[11px] text-background-400">/ 10</span>
                </span>
                <span className="flex items-center gap-1.5 text-xs text-background-300">
                  <i className="ri-fire-line text-sm w-4 h-4 flex items-center justify-center text-primary-400" />
                  {movie.views} 万次播放
                </span>
                <span className="flex items-center gap-1.5 text-xs text-background-300">
                  <i className="ri-calendar-line text-sm w-4 h-4 flex items-center justify-center" />
                  {movie.year} 年上映
                </span>
                <span className="flex items-center gap-1.5 text-xs text-background-300">
                  <i className="ri-time-line text-sm w-4 h-4 flex items-center justify-center" />
                  {formatDuration(movie.duration)}
                </span>
              </div>

              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-200">
                {movie.synopsis}
              </p>

              <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-2 text-xs sm:grid-cols-2">
                <div className="flex gap-2">
                  <dt className="shrink-0 text-background-400">导演</dt>
                  <dd className="text-background-100">{movie.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="shrink-0 text-background-400">地区</dt>
                  <dd className="text-background-100">{movie.region}</dd>
                </div>
                <div className="flex gap-2 sm:col-span-2">
                  <dt className="shrink-0 text-background-400">主演</dt>
                  <dd className="text-background-100">{movie.cast.join(" / ")}</dd>
                </div>
              </dl>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setPlayerOpen(true)}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-fill text-base w-4 h-4 flex items-center justify-center" />
                  播放演示
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-3 text-sm text-background-100 transition-colors hover:border-background-50/60 hover:bg-background-50/10"
                >
                  <i className="ri-share-forward-line text-base w-4 h-4 flex items-center justify-center" />
                  分享影片
                </button>
                <Link
                  to={`/category?type=${encodeURIComponent(movie.genres[0])}`}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-background-300 transition-colors hover:text-background-50"
                >
                  更多{movie.genres[0]}电影
                  <i className="ri-arrow-right-line text-sm w-4 h-4 flex items-center justify-center" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.65fr_1fr]">
          <div className="flex flex-col gap-6">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <h2 className="text-lg font-bold text-foreground-950">剧情简介</h2>
              <p className="mt-3 text-sm leading-7 text-foreground-700">{movie.synopsis}</p>
              <p className="mt-4 rounded-md bg-background-100 px-4 py-3 text-xs leading-relaxed text-foreground-500">
                {movie.highlight}
              </p>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-bold text-foreground-950">在线播放演示</h2>
                <span className="rounded-full bg-secondary-100 px-3 py-1 text-[11px] text-secondary-900">
                  前端演示 · 非真实播放
                </span>
              </div>

              <button
                type="button"
                onClick={() => setPlayerOpen(true)}
                className="group relative mt-4 block aspect-video w-full cursor-pointer overflow-hidden rounded-lg bg-foreground-950"
              >
                <img
                  src={movie.poster}
                  alt={`${movie.title} 播放区域封面`}
                  className="h-full w-full object-cover object-top opacity-35 blur-[2px] transition-transform duration-700 group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center px-5 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform duration-300 group-hover:scale-110">
                    <i className="ri-play-fill text-2xl w-6 h-6 flex items-center justify-center" />
                  </span>
                  <p className="mt-4 text-sm font-medium text-background-50">播放功能为前端演示，后续接入中</p>
                  <p className="mt-1.5 max-w-sm text-xs leading-relaxed text-background-300">
                    点击查看演示播放器界面，站内不加载真实视频流。
                  </p>
                </div>
                <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 bg-gradient-to-t from-foreground-950/90 to-transparent px-4 py-3">
                  <span className="font-label text-[11px] text-background-300">00:00</span>
                  <span className="relative h-1 flex-1 overflow-hidden rounded-full bg-background-50/25">
                    <span className="absolute inset-y-0 left-0 w-0 rounded-full bg-primary-500" />
                  </span>
                  <span className="font-label text-[11px] text-background-300">
                    {movie.duration}:00
                  </span>
                </div>
              </button>

              <p className="mt-3 text-xs leading-relaxed text-foreground-500">
                说明：本区域仅用于展示播放器交互位置。接入真实片源服务前，不会播放任何视频内容，也不会记录观看进度。
              </p>
            </section>
          </div>

          <aside className="flex flex-col gap-6">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="text-sm font-bold text-foreground-950">影片资料</h2>
              <dl className="mt-4 divide-y divide-background-200 text-xs">
                {metaRows.map((row) => (
                  <div key={row.label} className="flex gap-3 py-2.5">
                    <dt className="w-16 shrink-0 text-foreground-500">{row.label}</dt>
                    <dd className="text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <section className="rounded-lg border border-primary-200 bg-primary-50 p-5 md:p-6">
              <h2 className="text-sm font-bold text-foreground-950">类型标签</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {movie.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category?type=${encodeURIComponent(genre)}`}
                    className="whitespace-nowrap rounded-full border border-primary-300/70 bg-background-50 px-3 py-1.5 text-xs text-primary-800 transition-colors hover:bg-primary-500 hover:text-background-50"
                  >
                    {genre}
                  </Link>
                ))}
                {movie.tags.map((tag) => (
                  <span
                    key={tag}
                    className="whitespace-nowrap rounded-full bg-accent-100 px-3 py-1.5 text-xs text-accent-900"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-4 py-2.5 text-sm text-primary-800 transition-colors hover:bg-primary-100"
              >
                <i className="ri-link-m text-base w-4 h-4 flex items-center justify-center" />
                复制分享链接
              </button>
            </section>
          </aside>
        </div>

        <section className="mt-12">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="font-label text-[11px] uppercase tracking-[0.24em] text-primary-600">
                You May Also Like
              </p>
              <h2 className="mt-2 text-xl font-bold text-foreground-950 md:text-2xl">相关推荐</h2>
              <p className="mt-2 text-sm text-foreground-600">
                根据类型与导演相似度挑选，点击可进入对应影片详情。
              </p>
            </div>
            <Link
              to={`/category?type=${encodeURIComponent(movie.genres[0])}`}
              className="shrink-0 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-700"
            >
              更多同类
              <i className="ri-arrow-right-line ml-1 text-sm w-4 h-4 inline-flex items-center justify-center" />
            </Link>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-7 sm:grid-cols-3 lg:grid-cols-4">
            {related.map((item, index) => (
              <MovieCard key={item.id} movie={item} index={index} />
            ))}
          </div>
        </section>
      </div>

      <PlayerDemo movie={movie} open={playerOpen} onClose={() => setPlayerOpen(false)} />
      <ShareDialog movie={movie} open={shareOpen} onClose={() => setShareOpen(false)} />
    </SiteLayout>
  );
}